import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Send, Loader2, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'diana_session_id';

/**
 * Diana — floating chat concierge for storefront visitors.
 * Hidden on admin/pos/barcode-sheet routes.
 */
export default function DianaChat() {
  const { pathname } = useLocation();
  const hiddenRoutes = ['/admin', '/pos', '/barcode-sheet', '/login', '/register'];
  const hidden = hiddenRoutes.some(r => pathname.startsWith(r));

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
  const scrollRef = useRef(null);

  // Load prior history on open
  useEffect(() => {
    if (!open || !sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/chat/diana/history`, { params: { session_id: sessionId }, withCredentials: true });
        if (!cancelled) setMessages(data.messages || []);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [open, sessionId]);

  // Auto-scroll
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, busy]);

  if (hidden) return null;

  const seed = messages.length === 0 && !busy ? [
    { role: 'assistant', content: "Hi, I'm Diana from Diosa Forever. I can help with your orders, tracking, returns, wishlist, or finding a piece. What do you need today?" }
  ] : [];

  const displayMessages = messages.length ? messages : seed;

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setBusy(true);
    setInput('');
    setMessages(m => [...m, { role: 'user', content: text }]);
    try {
      const { data } = await axios.post(`${API}/api/chat/diana/message`,
        { message: text, session_id: sessionId },
        { withCredentials: true });
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem(STORAGE_KEY, data.session_id);
      }
      setMessages(m => [...m, { role: 'assistant', content: data.reply }]);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Diana is momentarily quiet — please try again.');
      setMessages(m => m.slice(0, -1)); // remove optimistic user msg on failure
      setInput(text);
    } finally { setBusy(false); }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#1B1815] text-white shadow-xl hover:bg-[#B08D57] transition-colors flex items-center justify-center group"
          data-testid="diana-open"
          aria-label="Chat with Diana"
        >
          <Sparkles size={22} />
          <span className="absolute -top-2 -left-2 bg-[#B08D57] text-white text-[9px] tracking-widest uppercase px-1.5 py-0.5">Diana</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed bottom-6 right-6 z-40 w-[92vw] max-w-md h-[70vh] max-h-[640px] bg-white shadow-2xl border border-[#E5E0D8] flex flex-col" data-testid="diana-panel">
          <div className="bg-[#1B1815] text-white px-5 py-3 flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#B08D57]">Concierge</p>
              <h4 className="text-lg" style={{ fontFamily: "'Bodoni Moda', serif" }}>Diana</h4>
            </div>
            <button onClick={() => setOpen(false)} className="hover:text-[#B08D57]" data-testid="diana-close" aria-label="Close">
              <X size={20} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FAF6EF]">
            {displayMessages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-3.5 py-2.5 text-[13px] leading-relaxed whitespace-pre-wrap ${
                  m.role === 'user'
                    ? 'bg-[#B08D57] text-white'
                    : 'bg-white border border-[#E5E0D8] text-[#1B1815]'
                }`}>
                  {m.content}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="px-3.5 py-2.5 bg-white border border-[#E5E0D8] text-[#595956] text-[12px] italic flex items-center gap-2">
                  <Loader2 size={12} className="animate-spin" /> Diana is choosing her words…
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-[#E5E0D8] p-3 bg-white">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder="Ask about a piece, an order, sizing, returns…"
                rows={1}
                className="flex-1 text-sm py-2 px-3 border border-[#E5E0D8] resize-none focus:outline-none focus:border-[#1B1815]"
                data-testid="diana-input"
                disabled={busy}
              />
              <button
                onClick={send}
                disabled={busy || !input.trim()}
                className="w-10 h-10 bg-[#1B1815] text-white hover:bg-[#B08D57] disabled:opacity-40 flex items-center justify-center"
                data-testid="diana-send"
                aria-label="Send"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </button>
            </div>
            <p className="text-[10px] text-[#8a8a86] mt-2">Diana can help with pieces, orders, care & returns. She never shares private data.</p>
          </div>
        </div>
      )}
    </>
  );
}
